import AllBrandsSearch from "src/organisms/AllBrandsSearch";
import type {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next/types";
import { withPage } from "src/utils/pagination/with-page";
import { allBrandsTranslations } from "src/utils/routeTranslations";
import getConfig from "next/config";
import { getNavigationItems } from "src/services/graphql/get-navigation-items";
import { getSeoContent } from "src/services/graphql/get-seo-content";
import { isGender, localeFix } from "src/utils/genderLocale/genderLocale";
import { ProductListingView } from "src/organisms/ProductListingView";
import { DEFAULT_PAGE_NOT_FOUND, PRODUCT_LIST_TYPE } from "src/utils/constants";
import type {
  CategoryProductList,
  ProductListProps,
} from "src/types/product-listing";
import type { NavigationItems, SeoQueryResponse } from "src/types/graphql.d";
import { getCategoryProductListProps } from "src/utils/search-provider/get-product-list-props";
import { buildStaticProps } from "src/utils/static-props/app-page-props";
import { getBrands } from "src/utils/search-provider/get-brands";
import type { AllBrandData } from "src/types/adapters/browse.type";

type AllBrandsPageProps = {
  isAllBrandsPage: true;
  locale: string;
  gender: string;
  slug: string;
  categoryPageId: string;
  breadcrumbs: { title: string; slug?: string }[];
  navigationItems?: NavigationItems | null;
  brands: AllBrandData[];
  seoContent?: SeoQueryResponse | null;
};

type BrandOrCategoriesPageProps =
  | ProductListProps<CategoryProductList>
  | AllBrandsPageProps;

function isAllBrandsPage(
  props: BrandOrCategoriesPageProps
): props is AllBrandsPageProps {
  return !!props.isAllBrandsPage;
}

export default function BrandOrCategoriesPage({
  pageProps,
}: {
  pageProps: BrandOrCategoriesPageProps;
}): JSX.Element {
  if (isAllBrandsPage(pageProps)) {
    return (
      <AllBrandsSearch
        genderId={pageProps.gender}
        locale={pageProps.locale}
        brands={pageProps.brands}
        seoContent={pageProps.seoContent}
      />
    );
  }

  return <ProductListingView defaultSorting="POPULARITY" {...pageProps} />;
}

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

const { serverRuntimeConfig } = getConfig();

export const getStaticProps = withPage(async function getStaticProps(
  {
    params,
  }: GetStaticPropsContext<{
    slug: string;
    category: string;
  }>,
  page
): Promise<GetStaticPropsResult<BrandOrCategoriesPageProps>> {
  if (!params?.slug || !params?.category) {
    return DEFAULT_PAGE_NOT_FOUND;
  }

  if (params.category && params.category === "search") {
    return {
      redirect: {
        permanent: true,
        destination: `/search/${params.slug}`,
      },
    };
  }

  if (allBrandsTranslations.includes(params.category)) {
    const gender = localeFix({ gender: params.slug });
    const [{ navigationItems }, brands, seoContent] = await Promise.all([
      getNavigationItems(gender),
      getBrands(serverRuntimeConfig.LOCALE, gender),
      getSeoContent(`/${params.slug}/${params.category}`),
    ]);

    return buildStaticProps(
      {
        isAllBrandsPage: true,
        locale: serverRuntimeConfig.LOCALE,
        gender: params.slug,
        slug: params.category,
        categoryPageId: "",
        breadcrumbs: [],
        navigationItems,
        brands,
        seoContent,
      },
      60 * 30
    );
  }

  const categoryPage = await getCategoryProductListProps({
    gender: params.slug,
    type: PRODUCT_LIST_TYPE.CATEGORY_PLP,
    slug: params.category,
    page,
  });

  // OPS-1455: `/{gender}/{same gender word}` (/mujer/mujer, /donna/donna) is the
  // URL marketing expects for the top-level PLP, but it only resolves where Oware
  // localized the root category slug. Send it to the canonical shop-type PLP
  // instead of 404ing. Checked *after* the lookup so a real category with a
  // gender-like slug (/mujer/dames on ES — it is in the sitemap) is untouched.
  if (
    "notFound" in categoryPage &&
    isGender(params.category) &&
    localeFix({ gender: params.category }) ===
      localeFix({ gender: params.slug })
  ) {
    return {
      redirect: {
        permanent: true,
        destination: `/${params.slug}/products`,
      },
    };
  }

  return categoryPage;
});
